import { useCallback, useEffect, useState } from 'react';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';
import {
  BuiltinAppointmentTypeOverride,
  CustomAppointmentType,
  customAppointmentTypeColorMap,
  setRuntimeBuiltinAppointmentTypeOverrides,
  setRuntimeCustomAppointmentTypeColors,
  setRuntimeCustomAppointmentTypeIcons,
  setRuntimeCustomAppointmentTypeLabels,
} from '../constants/appointmentTypes';
import { useAuth } from '../contexts/AuthContext';

const KEY_RE = /^custom_[a-z0-9_]{4,43}$/;
const HEX_COLOR_RE = /^#[0-9A-Fa-f]{6}$/;

function normalizeCustomTypes(raw: unknown): CustomAppointmentType[] {
  if (!Array.isArray(raw)) return [];
  const out: CustomAppointmentType[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const key = String((item as any).key || '').trim();
    const label = String((item as any).label || '').trim();
    const color = String((item as any).color || '').trim();
    const icon = String((item as any).icon || '').trim().toLowerCase();
    if (!KEY_RE.test(key) || !label || !HEX_COLOR_RE.test(color)) continue;
    const row: CustomAppointmentType = { key, label, color: color.toUpperCase() };
    if (icon) row.icon = icon;
    out.push(row);
  }
  return out;
}

function normalizeOverrides(raw: unknown): Record<string, BuiltinAppointmentTypeOverride> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, BuiltinAppointmentTypeOverride> = {};
  for (const [rawKey, rawVal] of Object.entries(raw as Record<string, unknown>)) {
    const key = String(rawKey || '').trim().toLowerCase();
    if (!key || KEY_RE.test(key)) continue;
    if (!rawVal || typeof rawVal !== 'object' || Array.isArray(rawVal)) continue;
    const partial: BuiltinAppointmentTypeOverride = {};
    const label = String((rawVal as any).label || '').trim();
    const color = String((rawVal as any).color || '').trim();
    const icon = String((rawVal as any).icon || '').trim().toLowerCase();
    if (label) partial.label = label;
    if (color && HEX_COLOR_RE.test(color)) partial.color = color.toUpperCase();
    if (icon) partial.icon = icon;
    if (Object.keys(partial).length > 0) out[key] = partial;
  }
  return out;
}

function applyRuntimeCatalog(
  types: CustomAppointmentType[],
  overrides: Record<string, BuiltinAppointmentTypeOverride>,
) {
  setRuntimeCustomAppointmentTypeLabels(Object.fromEntries(types.map((t) => [t.key, t.label])));
  setRuntimeCustomAppointmentTypeColors(customAppointmentTypeColorMap(types));
  setRuntimeCustomAppointmentTypeIcons(
    Object.fromEntries(types.filter((t) => t.icon).map((t) => [t.key, t.icon!])),
  );
  setRuntimeBuiltinAppointmentTypeOverrides(overrides);
}

export function isCustomAppointmentTypeKey(key: string): boolean {
  return KEY_RE.test(String(key || '').trim().toLowerCase());
}

export function useAgendaCustomAppointmentTypes() {
  const { user } = useAuth();
  const unitId = user?.current_unit?.id || '';
  const [customTypes, setCustomTypes] = useState<CustomAppointmentType[]>([]);
  const [builtinOverrides, setBuiltinOverrides] = useState<Record<string, BuiltinAppointmentTypeOverride>>({});
  const [loading, setLoading] = useState(false);

  const applyCatalog = useCallback((types: CustomAppointmentType[], overrides: Record<string, BuiltinAppointmentTypeOverride>) => {
    setCustomTypes(types);
    setBuiltinOverrides(overrides);
    applyRuntimeCatalog(types, overrides);
  }, []);

  const refresh = useCallback(async () => {
    if (!unitId) {
      applyCatalog([], {});
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(getApiUrl('/clinic/settings'));
      const types = normalizeCustomTypes(res.data?.data?.agenda_custom_appointment_types);
      const overrides = normalizeOverrides(res.data?.data?.agenda_builtin_appointment_type_overrides);
      applyCatalog(types, overrides);
    } catch {
      applyCatalog([], {});
    } finally {
      setLoading(false);
    }
  }, [applyCatalog, unitId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onUnit = () => { void refresh(); };
    window.addEventListener('unit:changed', onUnit);
    return () => window.removeEventListener('unit:changed', onUnit);
  }, [refresh]);

  const createType = useCallback(
    async (label: string, color: string, icon?: string): Promise<CustomAppointmentType> => {
      const res = await axios.post(getApiUrl('/clinic/agenda/custom-appointment-types'), { label, color, icon });
      const created = res.data?.data?.created as CustomAppointmentType | undefined;
      await refresh();
      window.dispatchEvent(new CustomEvent('clinic-settings:saved'));
      if (!created?.key) throw new Error('Resposta inválida ao criar tipo');
      return created;
    },
    [refresh],
  );

  const updateCustomType = useCallback(
    async (key: string, label: string, color: string, icon?: string): Promise<void> => {
      await axios.patch(getApiUrl(`/clinic/agenda/custom-appointment-types/${encodeURIComponent(key)}`), {
        label,
        color,
        icon,
      });
      await refresh();
      window.dispatchEvent(new CustomEvent('clinic-settings:saved'));
    },
    [refresh],
  );

  const deleteCustomType = useCallback(
    async (key: string): Promise<void> => {
      await axios.delete(getApiUrl(`/clinic/agenda/custom-appointment-types/${encodeURIComponent(key)}`));
      await refresh();
      window.dispatchEvent(new CustomEvent('clinic-settings:saved'));
    },
    [refresh],
  );

  const saveBuiltinOverride = useCallback(
    async (key: string, patch: BuiltinAppointmentTypeOverride): Promise<void> => {
      await axios.put(getApiUrl(`/clinic/agenda/appointment-type-overrides/${encodeURIComponent(key)}`), patch);
      await refresh();
      window.dispatchEvent(new CustomEvent('clinic-settings:saved'));
    },
    [refresh],
  );

  const clearBuiltinOverride = useCallback(
    async (key: string): Promise<void> => {
      await axios.delete(getApiUrl(`/clinic/agenda/appointment-type-overrides/${encodeURIComponent(key)}`));
      await refresh();
      window.dispatchEvent(new CustomEvent('clinic-settings:saved'));
    },
    [refresh],
  );

  return {
    customTypes,
    builtinOverrides,
    loading,
    refresh,
    createType,
    updateCustomType,
    deleteCustomType,
    saveBuiltinOverride,
    clearBuiltinOverride,
  };
}
